import { useState } from 'react'
import {
  Building2,
  Clapperboard,
  Film,
  HeartPulse,
  Layers,
  Laugh,
  Mic2,
  Radio,
  Sparkles,
  Trophy,
} from 'lucide-react'
import BrandLogo from '../components/BrandLogo'
import {
  SlideCrossReferenceCore,
  SlidePlatformRails,
  SlideRelationalIntelligence,
} from './deck/CrossReferenceDeckSlides'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Multi-industry narrative deck (/deck-multi-industry)
   Cross-vertical positioning: creator industries today → expansion beyond.
   ═══════════════════════════════════════════════════════════════════════════ */

const DECK_PASSWORD = 'dizee2026'

export default function MultiIndustryDeckPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === DECK_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  if (!authenticated) {
    return (
      <div className="deck-final-login fixed inset-0 flex items-center justify-center p-4 sm:p-8" style={{ backgroundColor: '#13120A' }}>
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-[#FF2300] rounded-sm flex-shrink-0" />
              <BrandLogo className="h-3 w-auto text-white" />
            </div>
            <p className="text-sm text-zinc-500">Enter the password to view this deck.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Password"
                autoFocus
                className="deck-final-input w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-base text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/50 focus:border-transparent"
              />
              {error && (
                <p className="mt-2 text-xs text-red-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="deck-final-input w-full rounded-lg bg-white px-4 py-3 text-base font-semibold text-zinc-950 hover:bg-zinc-100 transition-colors"
            >
              View Deck
            </button>
          </form>
          <p className="mt-8 text-[11px] text-zinc-700">
            Confidential — For intended recipients only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen text-white deck-print-root pb-0 mb-0" style={{ backgroundColor: '#13120A' }}>
      <div className="fixed top-4 right-4 sm:top-6 sm:right-8 z-50">
        <span className="text-[9px] sm:text-[10px] font-medium text-zinc-800 uppercase tracking-widest">
          Confidential
        </span>
      </div>
      <div className="deck-container w-full max-w-[1920px] mx-auto mb-0 pb-0">
        {SLIDES.map(({ component: Section }, i) => (
          <section
            key={i}
            className="deck-slide w-full flex flex-col border-b border-white/[0.04] min-h-[100dvh] sm:min-h-0 sm:h-[1080px]"
          >
            <div className="slide-inner flex-1 flex flex-col justify-center w-full min-w-0 px-4 py-8 sm:px-8 sm:py-12 md:px-12 md:py-16 lg:px-20 lg:py-20 xl:px-24 xl:py-20">
              <Section />
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   LAYOUT & SHARED
   ═══════════════════════════════════════════════════════════════════════════ */

interface Slide { title: string; component: React.FC }

function SlideTag({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`inline-block text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest ${className}`}>
      {children}
    </span>
  )
}

function TwoCol({
  tag,
  headline,
  headlineNode,
  highlight,
  subline,
  children,
  right,
}: {
  tag?: string
  headline?: string
  headlineNode?: React.ReactNode
  highlight?: string
  subline?: string
  children?: React.ReactNode
  right: React.ReactNode
}) {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-w-0">
      <div className="min-w-0">
        {tag && <SlideTag>{tag}</SlideTag>}
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08] break-words">
          {headlineNode ?? (
            <>
              {headline ?? ''}
              {highlight && <span className="text-[#FF2300]">{highlight}</span>}
            </>
          )}
        </h2>
        {subline && <p className="mt-4 text-base sm:text-lg text-zinc-400 break-words">{subline}</p>}
        <div className="min-w-0">{children}</div>
      </div>
      <div className="flex items-center justify-center min-h-[280px] lg:min-h-[340px] min-w-0">
        {right}
      </div>
    </div>
  )
}

function GrayBox({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-zinc-800/40 p-5 min-w-0">
      <h3 className="text-[15px] font-semibold text-white mb-2">{title}</h3>
      <p className="text-[14px] sm:text-base text-zinc-400 leading-relaxed break-words">{children}</p>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDES
   ═══════════════════════════════════════════════════════════════════════════ */

function SlideTitle() {
  return (
    <div>
      <div className="flex items-center gap-5 mb-8">
        <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-[#FF2300] rounded-sm flex-shrink-0" aria-hidden />
        <BrandLogo className="h-10 sm:h-12 lg:h-14 w-auto text-white" />
      </div>
      <SlideTag className="mb-4">Multi-industry</SlideTag>
      <p className="mt-2 text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight text-white leading-snug max-w-3xl">
        One intelligent operating system for{' '}
        <span className="text-[#FF2300]">high-stakes creative careers</span>
        {' — '}not one vertical.
      </p>
      <p className="mt-6 text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed">
        DIZEE started where the pain was loudest: music, comedy, sports, and on-screen talent. The same
        fragmentation and coordination gaps repeat across industries that run on people, schedules, deals,
        and narrative — not just spreadsheets. The strategic upside is also{' '}
        <span className="text-zinc-300">cross-referenced data</span>: one graph of career reality that
        compounds insight as more signal lands in the same system.
      </p>
      <div className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-2 text-base sm:text-[17px] text-white">
        <span className="inline-flex items-center rounded-full border border-[#FF2300]/30 bg-[#FF2300]/10 px-4 py-2 text-[14px] sm:text-[15px] font-medium text-[#FF2300]">
          Strategy
        </span>
        <span>Spring 2026</span>
        <span>dizee.com</span>
      </div>
    </div>
  )
}

const ANCHOR_VERTICALS: { label: string; body: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  {
    label: 'Music',
    body: 'Releases, touring, rights, and teams — high volume, many tools, one public career arc.',
    Icon: Mic2,
  },
  {
    label: 'Comedy & live',
    body: 'Dates, merch, and fan touchpoints with the same “always on” coordination load as touring artists.',
    Icon: Laugh,
  },
  {
    label: 'Athletes',
    body: 'Brand, calendar, and stakeholder complexity — parallel to entertainment, different labels.',
    Icon: Trophy,
  },
  {
    label: 'Actors & on-screen talent',
    body: 'Projects, press, and representation — episodic work with the need for a single coherent story.',
    Icon: Clapperboard,
  },
]

function SlideAnchors() {
  return (
    <TwoCol
      tag="Shared pattern"
      headline="The same structural job across "
      highlight="creator verticals"
      subline="These worlds look different on the surface; underneath, teams juggle the same problems: fragmented tools, sensitive context, and alignment across agents, managers, and partners."
      right={
        <div className="w-full max-w-lg grid grid-cols-2 gap-3 sm:gap-4">
          {ANCHOR_VERTICALS.map(({ label, body, Icon }) => (
            <div
              key={label}
              className="rounded-xl border border-white/[0.08] bg-zinc-800/30 p-4 sm:p-5 flex flex-col gap-3"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#FF2300]/15 text-[#FF2300]">
                <Icon className="h-5 w-5" aria-hidden />
              </div>
              <p className="text-[15px] font-semibold text-white">{label}</p>
              <p className="text-[13px] sm:text-[14px] text-zinc-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      }
    >
      <div className="mt-8 grid sm:grid-cols-2 gap-4 min-w-0">
        <GrayBox title="Fragmented stacks">Inbox, docs, chat, and industry-specific apps rarely share one timeline or source of truth.</GrayBox>
        <GrayBox title="Public + private">Careers run on signal the world sees and detail that never leaves the inner circle — both need one system.</GrayBox>
      </div>
    </TwoCol>
  )
}

const EXPANSION_AREAS: { label: string; body: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  {
    label: 'Media & publishing',
    body: 'Authors, podcasters, and journalists — project-based work with audience and rights in the mix.',
    Icon: Radio,
  },
  {
    label: 'Wellness & personality-led brands',
    body: 'Coaches, clinicians with public profiles, and creators who monetize trust at scale.',
    Icon: HeartPulse,
  },
  {
    label: 'Talent-led businesses',
    body: 'Founders and operators whose “product” is their reputation, calendar, and network.',
    Icon: Building2,
  },
  {
    label: 'Film & episodic',
    body: 'Longer arcs of development, financing, and promotion — same need for alignment across stakeholders.',
    Icon: Film,
  },
]

function SlideExpansion() {
  return (
    <TwoCol
      tag="Beyond entertainment"
      headline="Expanding where the "
      highlight="same OS applies"
      subline="DIZEE is not locked to a single lane. Any industry where careers are coordinated, narrative-driven, and tool-heavy can sit on the same foundation."
      right={
        <div className="relative w-full max-w-lg aspect-square max-h-[420px] flex items-center justify-center">
          <div className="absolute inset-8 rounded-full border border-[#FF2300]/20 bg-[#FF2300]/[0.04]" aria-hidden />
          <div className="relative z-10 flex h-28 w-28 sm:h-32 sm:w-32 items-center justify-center rounded-2xl border border-white/[0.12] bg-zinc-800/60 shadow-xl">
            <Layers className="h-14 w-14 sm:h-16 sm:w-16 text-[#FF2300]" aria-hidden />
          </div>
          <Sparkles className="absolute top-6 right-8 h-6 w-6 text-[#FF2300]/60" aria-hidden />
          <Sparkles className="absolute bottom-10 left-10 h-5 w-5 text-zinc-500" aria-hidden />
        </div>
      }
    >
      <div className="mt-8 grid sm:grid-cols-2 gap-4 min-w-0">
        {EXPANSION_AREAS.map(({ label, body, Icon }) => (
          <div key={label} className="flex gap-3 rounded-xl border border-white/[0.06] bg-zinc-900/40 p-4 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-[#FF2300]">
              <Icon className="h-4 w-4" aria-hidden />
            </div>
            <div className="min-w-0">
              <p className="text-[14px] font-semibold text-white">{label}</p>
              <p className="mt-1 text-[13px] text-zinc-400 leading-relaxed">{body}</p>
            </div>
          </div>
        ))}
      </div>
    </TwoCol>
  )
}

function SlideClosing() {
  return (
    <div className="flex flex-col justify-center max-w-4xl">
      <SlideTag>One foundation</SlideTag>
      <h2 className="mt-6 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tighter text-white leading-[1.2]">
        A single layer for{' '}
        <span className="text-[#FF2300]">context, coordination, and truth</span>
        {' — '}so every vertical builds on the same core instead of reinventing the stack.
      </h2>
      <p className="mt-6 text-base sm:text-lg text-zinc-400 leading-relaxed max-w-3xl">
        Music and adjacent creator industries are the wedge. Multi-industry is the product thesis: one system
        that respects how talent businesses actually run — then extends wherever that pattern repeats. Unified
        data plus cross-reference is what makes intelligence and downstream AI disproportionately valuable —
        not a feature list bolted onto siloed tools.
      </p>
    </div>
  )
}

const SLIDES: Slide[] = [
  { title: 'Title', component: SlideTitle },
  { title: 'Creator verticals', component: SlideAnchors },
  { title: 'Expansion', component: SlideExpansion },
  { title: 'Platform rails', component: SlidePlatformRails },
  { title: 'Cross-reference', component: SlideCrossReferenceCore },
  { title: 'Intelligence', component: SlideRelationalIntelligence },
  { title: 'Closing', component: SlideClosing },
]
